import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useEvent } from "../../contexts/EventContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading, Subheading } from "../../design/heading";
import { Link } from "../../design/link";
import { Divider } from "../../design/divider";
import EventTypeBadge from "../../components/EventTypeBadge";
import DeleteEventButton from "./components/DeleteEventButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import EditEventButton from "./components/EditEventButton";
import EventSubtypeBadge from "../../components/EventSubtypeBadge";
import { useParticipant } from "../../contexts/ParticipantContext";
import EventFilesList from "./components/Files/EventFilesList";
import { useSeenState } from "../../contexts/SeenStateContext";
import ErrorView from "../../components/ErrorView";
import { useEventType } from "../../contexts/EventTypeContext";
import Breadcrumbs from "../../components/Breadcrumbs";
import SlateTextArea from "../../components/SlateTextArea/SlateTextArea";
import EventCategoryBadge from "../../components/EventCategoryBadge";
import EventButton from "../../components/EventButton";
import EventTagBadge from "../../components/EventTagBadge";
import { useEventTag } from "../../contexts/EventTagContext";

const EventDetailPage: React.FC = () => {
  const {eventTags} = useEventTag();
  const { participants } = useParticipant();
  const { eventCategories, eventTypes, eventSubtypes } = useEventType();
  const { recordSeenState } = useSeenState();
  const { eventId } = useParams<{ eventId: string }>();
  const { selectedEvent, setSelectedEvent, error, loading } = useEvent();

  useEffect(() => {
    if (eventId) {
      setSelectedEvent(eventId);
      recordSeenState({ eventId });
    }
  }, [eventId]);

  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedEvent) {
    return <ErrorView error={new Error("Event not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Events", href: "/events" },
          { label: selectedEvent._id, href: `/event/${selectedEvent._id}` },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>Event Detail</Heading>
              <div className="flex gap-2">
                <EventButton event={selectedEvent} />
                <DeleteEventButton eventId={selectedEvent._id} />
              </div>
            </div>
          </div>
          <Divider className="mt-2" />
          <DescriptionList>
            {selectedEvent.eventCategory && (
              <>
                <DescriptionTerm>Category</DescriptionTerm>
                <DescriptionDetails>
                  <EventCategoryBadge
                    link
                    eventCategoryId={selectedEvent.eventCategory}
                    eventCategories={eventCategories}
                  />
                </DescriptionDetails>
              </>
            )}
            {selectedEvent.eventType && (
              <>
                <DescriptionTerm>Type</DescriptionTerm>
                <DescriptionDetails>
                  <EventTypeBadge
                    link
                    eventTypeId={selectedEvent.eventType}
                    eventTypes={eventTypes}
                  />
                </DescriptionDetails>
              </>
            )}
            {selectedEvent.eventSubtype && (
              <>
                <DescriptionTerm>Type</DescriptionTerm>
                <DescriptionDetails>
                  <EventSubtypeBadge
                    link
                    eventSubtypeId={selectedEvent.eventSubtype}
                    eventSubtypes={eventSubtypes}
                  />
                </DescriptionDetails>
              </>
            )}
            {
              selectedEvent.eventTags && selectedEvent.eventTags.length > 0 && (
                <>
                  <DescriptionTerm>Tags</DescriptionTerm>
                  <DescriptionDetails>
                    {selectedEvent.eventTags.map((tag) => <EventTagBadge
                      key={tag}
                      eventTagId={tag}
                      eventTags={eventTags}
                      link
                    />)}
                  </DescriptionDetails>
                </>
              )
            }

            {selectedEvent.participants &&
              selectedEvent.participants.length > 0 && (
                <>
                  <DescriptionTerm>People Involved</DescriptionTerm>
                  <DescriptionDetails className="flex gap-2">
                    {
                      // Map to buttons
                      Array.from(selectedEvent.participants).map((pId) => {
                        const participant = (participants || []).find(
                          (p) => p._id === pId,
                        );
                        if (!participant) return null;
                        return (
                          <Link
                            key={participant._id}
                            href={`/participant/${participant._id}`}
                            className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400"
                          >
                            {participant.name}
                          </Link>
                        );
                      })
                    }
                  </DescriptionDetails>
                </>
              )}

            <DescriptionTerm>Date</DescriptionTerm>
            <DescriptionDetails>
              {moment(selectedEvent.eventDate).format("MMMM Do YYYY, h:mm a")}
            </DescriptionDetails>
            {selectedEvent.description && (
              <>
                <DescriptionTerm>Description</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                  <SlateTextArea
                    id={selectedEvent._id}
                    placeholder="Write a reply..."
                    initialValue={[...JSON.parse(selectedEvent.content)]}
                    readOnly={true}
                  />
                </DescriptionDetails>
              </>
            )}
            {selectedEvent.significance && (
              <>
                <DescriptionTerm>Significance</DescriptionTerm>
                <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                  {selectedEvent.significance}
                </DescriptionDetails>
              </>
            )}
            {selectedEvent.fileSection && (
              <>
                <DescriptionTerm>Citation</DescriptionTerm>
                <DescriptionDetails>
                  <div className="flex flex-col gap-1">
                    {/* Optional page number or metadata */}
                    <span className="text-xs text-zinc-400">
                      Page {selectedEvent.fileSection.page} of{" "}
                      {selectedEvent.fileSection.file?.name}
                    </span>

                    {/* Preview with limited lines */}
                    <blockquote className="pl-4 italic border-l-4 text-zinc-600 border-zinc-300 line-clamp-3">
                      {selectedEvent.fileSection.text}
                    </blockquote>

                    {selectedEvent.fileSection.file && (
                      <Link
                        href={`/file/${selectedEvent.fileSection.file._id}`}
                        className="text-sm font-medium text-blue-600 hover:underline"
                      >
                        View full citation →
                      </Link>
                    )}
                  </div>
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
          <Subheading>Supporting Files</Subheading>
          <EventFilesList eventId={selectedEvent._id} />
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
